import {
  BORROWER_TYPE_OPTIONS,
  CREDIT_PRODUCT_TYPE_CODES,
  CREDIT_PRODUCT_TYPE_OPTIONS,
  EXPENSE_REPORT_LINE_CODES,
  LIABILITY_REPORT_LINE_CODES,
  REPORT_PERIOD_OPTIONS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import {
  BorrowerType,
  type TFinancialConditionCertificateResponse,
  type TFinancialConditionCertificateDto,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  createBankAverageBalanceRow,
  createBankIncomeRow,
  createBudgetDebt,
  createCourtCase,
  createCreditOperation,
  createCurrencyExchange,
  createExpense,
  createFinancialAssistance,
  createFinancialInvestment,
  createIncomeBreakdown,
  createLiability,
  createLoanOrBond,
  createOffBalanceLiability,
  createReceivable,
  createRowsWithReportLines,
  formatThousandAmount,
  getFinancialConditionDefaultValues,
  normalizeBalanceLine,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import type {
  TDocumentGenerationBank,
  TDocumentGenerationCurrency,
} from '~/store/covenants/types.ts';
import { formatDateForDisplay } from '~/helpers/utils.ts';

type TBankNameVariant = 'full' | 'short';

const getBankName = (bank: TDocumentGenerationBank, variant: TBankNameVariant): string | undefined =>
  variant === 'short'
    ? bank.shortname ?? bank.sname ?? bank.name ?? bank.fullName ?? undefined
    : bank.fullName ?? bank.name ?? bank.shortname ?? bank.sname ?? undefined;

const findBank = (
  bankName: string | undefined,
  bankCode: string | null | undefined,
  banks: TDocumentGenerationBank[]
): TDocumentGenerationBank | undefined => {
  const normalizedName = bankName?.trim().toLocaleLowerCase();

  return banks.find(
    (bank) =>
      (bankCode && String(bank.idNbu ?? '') === bankCode) ||
      (!!normalizedName &&
        [bank.fullName, bank.name, bank.shortname, bank.sname].some(
          (name) => name?.trim().toLocaleLowerCase() === normalizedName
        ))
  );
};

const getBankOption = (
  bankName: string | undefined,
  bankCode: string | null | undefined,
  banks: TDocumentGenerationBank[],
  variant: TBankNameVariant
): TAutocompleteOption | null => {
  if (!bankName?.trim()) return null;

  const bank = findBank(bankName, bankCode, banks);

  return bank
    ? { value: String(bank.id), label: getBankName(bank, variant) ?? bankName }
    : { value: '-1', label: bankName };
};

const getCurrencyOption = (
  currencyCode: string | undefined,
  currencies: TDocumentGenerationCurrency[]
): TAutocompleteOption | null => {
  if (!currencyCode) return null;

  const currency = currencies.find(
    (item) => String(item.id) === currencyCode || item.symbolCode === currencyCode
  );

  return currency?.symbolCode?.trim()
    ? { value: String(currency.id), label: currency.symbolCode.trim() }
    : null;
};

const getProductTypeOption = (productType: string | undefined): TAutocompleteOption | null => {
  if (!productType) return null;

  const optionValue = Object.entries(CREDIT_PRODUCT_TYPE_CODES).find(
    ([, value]) => value === productType
  )?.[0];

  return CREDIT_PRODUCT_TYPE_OPTIONS.find((option) => option.value === optionValue) ?? null;
};

const toInputValue = (value: number | undefined): string =>
  value === undefined || value === null ? '' : formatThousandAmount(value);

const toTextValue = (value: string | undefined): string => value ?? '';

const toRows = <T,>(rows: T[] | undefined, createRow: () => T): T[] =>
  rows?.length ? rows : [createRow()];

const getReportLineCode = (reportLine: string): string => reportLine.replace(/\D/g, '');

const addMissingTemplateRows = <T extends { reportLine: string }>(
  rows: T[],
  reportLineCodes: readonly string[],
  createRow: () => T
): T[] => {
  const templateRows = reportLineCodes.flatMap((reportLineCode) => {
    const matchingRows = rows.filter(
      (row) => getReportLineCode(row.reportLine) === reportLineCode
    );

    return matchingRows.length
      ? matchingRows
      : createRowsWithReportLines([reportLineCode], createRow);
  });
  const customRows = rows.filter(
    (row) => !reportLineCodes.includes(getReportLineCode(row.reportLine))
  );

  return [...templateRows, ...customRows];
};

const getReportPeriodValues = (reportPeriod: string | undefined) => {
  const yearMatch = reportPeriod?.match(/\b(?:19|20)\d{2}\b/)?.[0];
  const defaults = getFinancialConditionDefaultValues();
  const normalizedPeriod = reportPeriod?.trim().toLocaleLowerCase('uk-UA') ?? '';

  return {
    reportPeriod:
      REPORT_PERIOD_OPTIONS.find((option) =>
        normalizedPeriod.startsWith(option.label.toLocaleLowerCase('uk-UA'))
      ) ?? defaults.reportPeriod,
    reportYear: yearMatch ?? defaults.reportYear,
  };
};

const BACKEND_MONTH_PERIOD_VALUES: Record<string, string> = {
  JANUARY: 'month:1',
  FEBRUARY: 'month:2',
  MARCH: 'month:3',
  APRIL: 'month:4',
  MAY: 'month:5',
  JUNE: 'month:6',
  JULY: 'month:7',
  AUGUST: 'month:8',
  SEPTEMBER: 'month:9',
  OCTOBER: 'month:10',
  NOVEMBER: 'month:11',
  DECEMBER: 'month:12',
};

const getReportPeriodOption = (childPeriod?: string): TAutocompleteOption | undefined => {
  if (!childPeriod?.trim()) {
    return undefined;
  }

  const normalizedPeriod = childPeriod.trim().toUpperCase();
  const monthPeriodValue = BACKEND_MONTH_PERIOD_VALUES[normalizedPeriod];
  const quarterMatch = normalizedPeriod.match(/^(?:([1-4])Q|Q([1-4]))$/);
  const optionValue =
    monthPeriodValue ?? (quarterMatch ? `quarter:${quarterMatch[1] ?? quarterMatch[2]}` : undefined);

  return REPORT_PERIOD_OPTIONS.find((option) => option.value === optionValue);
};

export const applyFinancialConditionCertificateGeneralInfo = (
  values: TFinancialConditionFormValues,
  response: TFinancialConditionCertificateResponse
): TFinancialConditionFormValues => {
  const borrowerType = response.fop ? BorrowerType.SoleTrader : BorrowerType.LegalEntity;

  return {
    ...values,
    borrowerType:
      BORROWER_TYPE_OPTIONS.find((option) => option.value === borrowerType) ?? null,
    borrowerName: response.fullName ?? values.borrowerName,
    borrowerCode: response.borrowerEdrpou ?? values.borrowerCode,
    reportPeriod: getReportPeriodOption(response.childPeriod) ?? values.reportPeriod,
    reportYear: response.yearPeriod || values.reportYear,
    reportDate: response.asOfDay ? formatDateForDisplay(response.asOfDay) || values.reportDate : values.reportDate,
  };
};

export const deserializeFinancialConditionCertificate = (
  certificate: TFinancialConditionCertificateDto,
  borrowerType: BorrowerType,
  banks: TDocumentGenerationBank[],
  currencies: TDocumentGenerationCurrency[]
): TFinancialConditionFormValues => {
  const defaults = getFinancialConditionDefaultValues();
  const { reportPeriod, reportYear } = getReportPeriodValues(certificate.reportPeriod);
  const cashInflows = certificate.cashInflows ?? [];
  const averageBalances = certificate.averageBalances ?? [];
  const loans = certificate.loans ?? [];

  return {
    ...defaults,
    borrowerType:
      BORROWER_TYPE_OPTIONS.find((option) => option.value === borrowerType) ?? null,
    borrowerName: toTextValue(certificate.borrowerFullName),
    borrowerCode: toTextValue(certificate.borrowerEdrpou),
    reportPeriod,
    reportYear,
    reportDate: certificate.reportDate ?? null,
    hasCashFlowTransferRequirement:
      borrowerType === BorrowerType.LegalEntity && cashInflows.length > 0,
    incomeBanks: toRows(
      cashInflows.map((row) => ({
        bankId: getBankOption(row.bankName, row.bankCode, banks, 'full'),
        currency: getCurrencyOption(row.currency, currencies),
        total: toInputValue(row.totalAmount),
        salesRevenue: toInputValue(row.revenueAmount),
      })),
      createBankIncomeRow
    ),
    hasAverageBalanceRequirement: averageBalances.length > 0,
    averageBalanceBanks: toRows(
      averageBalances.map((row) => ({
        bankId: getBankOption(row.bankName, row.bankCode, banks, 'full'),
        currency: getCurrencyOption(row.currency, currencies),
        firstMonthAmount: toInputValue(row.month1Amount),
        secondMonthAmount: toInputValue(row.month2Amount),
        thirdMonthAmount: toInputValue(row.month3Amount),
      })),
      createBankAverageBalanceRow
    ),
    creditOperations: toRows(
      (certificate.creditOperations ?? []).map((row) => ({
        lenderName: getBankOption(row.creditorName, undefined, banks, 'short'),
        productType: getProductTypeOption(row.productType),
        currency: getCurrencyOption(row.currency, currencies),
        limit: toInputValue(row.limitAmount),
        outstandingDebt: toInputValue(row.debtAmount),
        interestRate: toInputValue(row.interestRate),
        issueDate: row.issueDate ?? null,
        maturityDate: row.maturityDate ?? null,
        overdueDebt: toInputValue(row.overdueAmount),
      })),
      createCreditOperation
    ),
    providedFinancialAssistance: toRows(
      (certificate.financialAidGiven ?? []).map((row) => ({
        counterpartyName: toTextValue(row.counterpartyName),
        currency: getCurrencyOption(row.currency, currencies),
        outstandingDebt: toInputValue(row.debtAmount),
        issueOrReceiptDate: row.issueDate ?? null,
        maturityDate: row.repaymentDate ?? null,
        balanceLine: normalizeBalanceLine(toTextValue(row.balanceLine)),
        overdueDebt: toInputValue(row.overdueAmount),
      })),
      createFinancialAssistance
    ),
    receivedFinancialAssistance: toRows(
      (certificate.financialAidReceived ?? []).map((row) => ({
        counterpartyName: toTextValue(row.counterpartyName),
        currency: getCurrencyOption(row.currency, currencies),
        outstandingDebt: toInputValue(row.debtAmount),
        issueOrReceiptDate: row.issueDate ?? null,
        maturityDate: row.repaymentDate ?? null,
        balanceLine: normalizeBalanceLine(toTextValue(row.balanceLine)),
        overdueDebt: toInputValue(row.overdueAmount),
      })),
      createFinancialAssistance
    ),
    receivedLoans: toRows(
      loans
        .filter((row) => row.indicator === 'Позики' || row.indicator === 'Позика')
        .map((row) => ({
          lenderName: getBankOption(row.creditorName, undefined, banks, 'short'),
          lenderCode: toTextValue(row.creditorEdrpou),
          receivedDate: row.receiveDate ?? null,
          maturityDate: row.repaymentDate ?? null,
          currency: getCurrencyOption(row.currency, currencies),
          currentAmount: toInputValue(row.currentAmount),
          balanceLine: normalizeBalanceLine(toTextValue(row.balanceLine)),
          overdueDebt: toInputValue(row.overdueAmount),
        })),
      createLoanOrBond
    ),
    bondLoans: toRows(
      loans
        .filter(
          (row) =>
            row.indicator === 'Облігаційні позики' || row.indicator === 'Облігаційна позика'
        )
        .map((row) => ({
          lenderName: getBankOption(row.creditorName, undefined, banks, 'short'),
          lenderCode: toTextValue(row.creditorEdrpou),
          receivedDate: row.receiveDate ?? null,
          maturityDate: row.repaymentDate ?? null,
          currency: getCurrencyOption(row.currency, currencies),
          currentAmount: toInputValue(row.currentAmount),
          balanceLine: normalizeBalanceLine(toTextValue(row.balanceLine)),
          overdueDebt: toInputValue(row.overdueAmount),
        })),
      createLoanOrBond
    ),
    offBalanceLiabilities: toRows(
      (certificate.offBalanceLiabilities ?? []).map((row) => ({
        debtor: toTextValue(row.debtor),
        creditor: getBankOption(row.creditor, undefined, banks, 'short'),
        obligationType: toTextValue(row.obligationType),
        currency: getCurrencyOption(row.currency, currencies),
        limitAmount: toInputValue(row.limitAmount),
        actualAmount: toInputValue(row.actualAmount),
        interestRate: toInputValue(row.interestRate),
        openDate: row.openDate ?? null,
        endDate: row.endDate ?? null,
      })),
      createOffBalanceLiability
    ),
    currencyExchanges: toRows(
      (certificate.currencyExchanges ?? []).map((row) => ({
        bank: getBankOption(row.bankName, row.bankCode, banks, 'full'),
        soldCurrency: getCurrencyOption(row.soldCurrency, currencies),
        soldAmount: toInputValue(row.soldAmount),
        boughtCurrency: getCurrencyOption(row.boughtCurrency, currencies),
        boughtAmount: toInputValue(row.boughtAmount),
      })),
      createCurrencyExchange
    ),
    incomeBreakdowns: toRows(
      (certificate.incomeBreakdowns ?? []).map((row) => ({
        counterpartyName: toTextValue(row.counterpartyName),
        counterpartyEdrpou: toTextValue(row.counterpartyEdrpou),
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
        incomeType: toTextValue(row.component),
        amount: toInputValue(row.amount),
        sharePercent: toInputValue(row.sharePercent),
      })),
      createIncomeBreakdown
    ),
    liabilitiesAnnual: addMissingTemplateRows(
      (certificate.liabilitiesAnnual ?? []).map((row) => ({
        creditor: getBankOption(row.creditor, undefined, banks, 'short'),
        liabilityType: toTextValue(row.liabilityType),
        amount: toInputValue(row.amount),
        purpose: toTextValue(row.purpose),
        originDate: row.originDate ?? null,
        repaymentDate: row.repaymentDate ?? null,
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
      })),
      LIABILITY_REPORT_LINE_CODES.liabilitiesAnnual,
      createLiability
    ),
    liabilitiesQuarterly: addMissingTemplateRows(
      (certificate.liabilitiesQuarterly ?? []).map((row) => ({
        creditor: getBankOption(row.creditor, undefined, banks, 'short'),
        liabilityType: toTextValue(row.liabilityType),
        amount: toInputValue(row.amount),
        purpose: toTextValue(row.purpose),
        originDate: row.originDate ?? null,
        repaymentDate: row.repaymentDate ?? null,
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
      })),
      LIABILITY_REPORT_LINE_CODES.liabilitiesQuarterly,
      createLiability
    ),
    financialInvestments: toRows(
      (certificate.financialInvestments ?? []).map((row) => ({
        name: toTextValue(row.name),
        edrpou: toTextValue(row.edrpou),
        sharePercent: toInputValue(row.sharePercent),
        amount: toInputValue(row.amount),
        startDate: row.startDate ?? null,
      })),
      createFinancialInvestment
    ),
    receivables: toRows(
      (certificate.receivables ?? []).map((row) => ({
        counterpartyName: toTextValue(row.counterpartyName),
        edrpou: toTextValue(row.edrpou),
        subject: toTextValue(row.subject),
        totalDebt: toInputValue(row.totalDebt),
        overdueAmount: toInputValue(row.overdueAmount),
        originDate: row.originDate ?? null,
        repaymentDate: row.repaymentDate ?? null,
      })),
      createReceivable
    ),
    expensesAnnual: addMissingTemplateRows(
      (certificate.expensesAnnual ?? []).map((row) => ({
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
        component: toTextValue(row.component),
        amount: toInputValue(row.amount),
        sharePercent: toInputValue(row.sharePercent),
      })),
      EXPENSE_REPORT_LINE_CODES.expensesAnnual,
      createExpense
    ),
    expensesQuarterly: addMissingTemplateRows(
      (certificate.expensesQuarterly ?? []).map((row) => ({
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
        component: toTextValue(row.component),
        amount: toInputValue(row.amount),
        sharePercent: toInputValue(row.sharePercent),
      })),
      EXPENSE_REPORT_LINE_CODES.expensesQuarterly,
      createExpense
    ),
    expensesQuarterlySmall: toRows(
      (certificate.expensesQuarterlySmall ?? []).map((row) => ({
        reportLine: normalizeBalanceLine(toTextValue(row.reportLine)),
        component: toTextValue(row.component),
        amount: toInputValue(row.amount),
        sharePercent: toInputValue(row.sharePercent),
      })),
      createExpense
    ),
    budgetDebts: toRows(
      (certificate.budgetDebts ?? []).map((row) => ({
        debtType: toTextValue(row.debtType),
        amount: toInputValue(row.amount),
      })),
      createBudgetDebt
    ),
    courtCases: toRows(
      (certificate.courtCases ?? []).map((row) => ({
        otherParty: toTextValue(row.otherParty),
        borrowerName: toTextValue(row.borrowerName),
        borrowerStatus: toTextValue(row.borrowerStatus),
        subject: toTextValue(row.subject),
        claimAmount: toInputValue(row.claimAmount),
        claimDate: row.claimDate ?? null,
        currentState: toTextValue(row.currentState),
      })),
      createCourtCase
    ),
    additionalInfo: toTextValue(certificate.additionalInfo),
  };
};
